// Стрелки направления течений. Слой течений рисует только МОДУЛЬ скорости,
// поэтому по картинке не видно, куда идёт вода.
//
// Разделение труда: цвет = модуль, стрелка = направление. Длиной скорость больше
// НЕ кодируется — двойное кодирование одной величины мешало читать поле, а
// короткие стрелки в слабых струях просто пропадали. Все стрелки одинаковые,
// мелкие, тонкие и чёрные; сетка плотная, чтобы прослеживались вихри.
//
// Шаг сетки задаётся не в градусах, а через постоянное расстояние на ЭКРАНЕ:
// иначе один и тот же шаг в градусах даёт на обзорном зуме кашу, а на рабочем —
// три стрелки на всю карту.

import { t } from '../i18n'

/** Расстояние между стрелками на экране, px. */
export const ARROW_SPACING_PX = 20

/** Длина стрелки, px (при масштабе 1). Одинакова у всех. */
export const ARROW_ICON_PX = 10

/** Толщина линии стрелки, px. */
export const ARROW_STROKE_PX = 1

/** Цвет стрелки — чёрный. Читается и на светлой, и на тёмной части шкалы
 *  течений (проверено на макете по данным прода). */
export const ARROW_COLOR = '#0f172a'

/** Доля расстояния между стрелками, которую занимает стрелка. Ровно половина:
 *  зазор между соседними стрелками равен длине стрелки — стрелка, пробел,
 *  стрелка. Это и задаёт ритм картинки. */
const ARROW_FILL = 0.5

/** Потолок роста стрелки. За z≈8.7 экранный шаг становится мельче родной сетки
 *  1/12°, и расстояние между стрелками растёт: гуще данных стрелки не поставить.
 *  Стрелка тянется за расстоянием, чтобы не превратиться в точки среди пустоты,
 *  но не безгранично — иначе на предельном зуме она станет жирной чертой. */
const ARROW_MAX_SCALE = 1.6

/** Порог: слабее — направление численный шум (тот же, что на бэке). */
export const MIN_SPEED_MS = 0.02

/** Пределы шага: мельче родной сетки прореживать нечего, крупнее 2° бессмысленно. */
const MIN_STEP_DEG = 1 / 12
const MAX_STEP_DEG = 2

/** Пикселей на градус долготы при данном зуме (Web Mercator, тайл 256). */
export function pxPerLonDegree(zoom: number): number {
  return (256 * Math.pow(2, zoom)) / 360
}

/** Шаг прореживания в градусах под текущий зум: столько градусов, сколько
 *  укладывается в ARROW_SPACING_PX на экране. */
export function arrowStepDeg(zoom: number): number {
  const raw = ARROW_SPACING_PX / pxPerLonDegree(zoom)
  return Math.min(MAX_STEP_DEG, Math.max(MIN_STEP_DEG, raw))
}

/** Фактическое расстояние между стрелками на экране, px. Равно
 *  ARROW_SPACING_PX везде, кроме зумов, где шаг упёрся в потолок 2°
 *  (обзорные) или в родную сетку 1/12° (очень крупные). */
export function arrowSpacingPx(zoom: number): number {
  return arrowStepDeg(zoom) * pxPerLonDegree(zoom)
}

/** Единый масштаб иконки на данном зуме — один для ВСЕХ стрелок карты.
 *
 *  Держит правило «зазор = длина стрелки» на любом зуме: на обзорных шаг
 *  упирается в 2° и расстояние падает, на предельных — в родную сетку 1/12°
 *  и растёт. Стрелка следует за расстоянием, а не живёт константой. */
export function arrowScale(zoom: number): number {
  return Math.min(ARROW_MAX_SCALE, (ARROW_FILL * arrowSpacingPx(zoom)) / ARROW_ICON_PX)
}

// ---------------------------------------------------------------------------
// Оверлей «Волнение»: высота волны красит стрелки #0f172a → красный
// ---------------------------------------------------------------------------
// Стрелки те же и той же длины (длина = только направление); меняется ЦВЕТ:
// штиль/нет данных — ink #0f172a (как сейчас), с ростом высоты волны краснеет.
// Реализуется набором предрисованных цветных образов стрелки (crisp, без SDF),
// выбор per-arrow — step-выражением icon-image по свойству wave_hs.

/** Ниже — штиль (цвет как сейчас, ink); выше WAVE_HS_MAX — насыщенный красный. */
export const WAVE_HS_MIN = 0.5
export const WAVE_HS_MAX = 5
/** Число ступеней рампы (образов стрелки). */
export const WAVE_ARROW_STEPS = 12

/** Цвет стрелки по нормированной высоте t∈[0,1]: ink #0f172a → тёмно-красный →
 *  красный (рампа согласованного макета). */
export function waveArrowColor(t: number): string {
  const c = Math.max(0, Math.min(1, t))
  const ink = [15, 23, 42]
  const mid = [138, 26, 20]
  const red = [228, 46, 34]
  const [a, b, k] = c < 0.5 ? [ink, mid, c / 0.5] : [mid, red, (c - 0.5) / 0.5]
  const hex = (i: number) =>
    Math.round(a[i] + (b[i] - a[i]) * k)
      .toString(16)
      .padStart(2, '0')
  return `#${hex(0)}${hex(1)}${hex(2)}`
}

export function waveArrowImageId(i: number): string {
  return `arrow-wave-${i}`
}

/** Ведёрки для step-выражения icon-image: нижний порог Hs (м) → id образа + цвет.
 *  Значения ниже первого порога (штиль/нет ветра) рисуются базовым ink-образом. */
export function waveArrowBuckets(): { hs: number; id: string; color: string }[] {
  const out: { hs: number; id: string; color: string }[] = []
  for (let i = 0; i < WAVE_ARROW_STEPS; i++) {
    const t = i / (WAVE_ARROW_STEPS - 1)
    out.push({
      hs: WAVE_HS_MIN + t * (WAVE_HS_MAX - WAVE_HS_MIN),
      id: waveArrowImageId(i),
      color: waveArrowColor(t),
    })
  }
  return out
}

// ---------------------------------------------------------------------------
// Честное падение
// ---------------------------------------------------------------------------

export type ArrowsState =
  | { kind: 'ok'; truncated?: boolean }
  | { kind: 'empty' }
  | { kind: 'error'; status?: number }

/** Подпись о состоянии слоя стрелок, `null` — всё в порядке.
 *
 *  Раньше ошибка запроса глушилась пустым catch: на карте оставалось поле
 *  ПРЕДЫДУЩЕЙ даты либо не было ничего, и отличить «нет данных» от «сломалось»
 *  было нельзя. Пустая карта без объяснения читается как баг отрисовки. */
export function arrowsNotice(state: ArrowsState): string | null {
  if (state.kind === 'empty') return t('models.arrows.noField')
  if (state.kind === 'error') {
    if (state.status === 401) return t('models.arrows.sessionExpired')
    return state.status
      ? t('models.arrows.httpError', { status: state.status })
      : t('models.arrows.noConnection')
  }
  return state.truncated ? t('models.arrows.truncated') : null
}
